import clsx from 'clsx';
import { AnimatePresence, motion } from 'motion/react';
import React, { useEffect, useRef, useState } from 'react';
export function ExpandableInput({
  placeholder = 'Ask Penny a question',
  className,
  id,
  value,
  onChange,
  maxLineHeight = 3,
  disabled = false,
  voiceIconClassName,
  submitIconClassName,
  variant = 'glow',
  voiceIcon,
  submitIcon,
  onSubmitClickHandler,
  onVoiceClickHandler
}) {
  const [inputValue, setInputValue] = useState(value || '');
  const textareaRef = useRef(null);
  const [isMultiLine, setIsMultiLine] = useState(false);
  const [textareaHeight, setTextareaHeight] = useState(32);
  const [isHovered, setIsHovered] = useState(false);
  // Auto-resize the textarea
  const adjustHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const scrollHeight = textarea.scrollHeight;
      const lineHeight = parseInt(window.getComputedStyle(textarea).lineHeight);
      const maxHeight = lineHeight * maxLineHeight;
      const hasMultipleLines = scrollHeight - 20 > lineHeight;
      setIsMultiLine(hasMultipleLines);
      let finalHeight;
      if (scrollHeight > maxHeight) {
        finalHeight = maxHeight;
        textarea.style.height = `${maxHeight}px`;
        textarea.style.overflowY = 'auto';
      } else {
        finalHeight = scrollHeight;
        textarea.style.height = `${scrollHeight}px`;
        textarea.style.overflowY = 'hidden';
      }
      setTextareaHeight(finalHeight);
    }
  };
  useEffect(() => {
    adjustHeight();
  }, [inputValue]);
  // Add the gradient style for the glow variant
  useEffect(() => {
    const styleId = 'subtle-gradient-style';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .subtle-glow:hover:not(:disabled),
        .subtle-glow:focus:not(:disabled) {
          outline: none;
          border: 1px solid transparent;
          background-image: linear-gradient(white, white), linear-gradient(to right, #7700BB, #5C18C7, #0068EF);
          background-origin: border-box;
          background-clip: padding-box, border-box;
          box-shadow: 0 0 4px rgba(119, 0, 187, 0.2);
        } 
        
         .no-glow:hover:not(:disabled),
      .no-glow:focus:not(:disabled) {
        border-color: #496785 !important;
        transition: border-color 0.2s ease;
      }


      `;
      document.head.appendChild(style);
    }
  }, []);
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [disabled, textareaRef]);
  // Input focus and value handling
  const setFocusTextarea = () => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };
  function handleChange(e) {
    const newValue = e.target.value;
    setInputValue(newValue);
    if (onChange) onChange(newValue);
  }
  function handleSubmit() {
    if (!onSubmitClickHandler || !inputValue.trim()) return;
    onSubmitClickHandler(inputValue);
    setInputValue('');
    setFocusTextarea();
  }
  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }
  // Button rendering
  const renderVoiceButton = () => React.createElement(motion.button, {
    key: 'voice-button',
    type: 'button',
    initial: {
      opacity: 0,
      scale: 0.8
    },
    animate: {
      opacity: 1,
      scale: 1
    },
    exit: {
      opacity: 0,
      scale: 0.8
    },
    transition: {
      duration: 0.075,
      ease: 'easeOut'
    },
    "aria-label": 'voice input',
    className: `absolute inset-0 ${voiceIconClassName}`,
    style: {
      borderRadius: '50%'
    },
    onClick: onVoiceClickHandler
  }, voiceIcon);
  const renderSubmitButton = () => React.createElement(motion.button, {
    key: 'send-button',
    type: 'button',
    initial: {
      opacity: 0,
      scale: 0.8
    },
    animate: {
      opacity: 1,
      scale: 1
    },
    exit: {
      opacity: 0,
      scale: 0.8
    },
    transition: {
      duration: 0.075,
      ease: 'easeOut'
    },
    "aria-label": 'send message',
    className: `absolute inset-0 ${submitIconClassName}`,
    onClick: handleSubmit,
    style: {
      borderRadius: '50%'
    }
  }, submitIcon);
  const renderButtons = () => {
    if (submitIcon && voiceIcon) {
      return React.createElement("div", {
        style: {
          maxHeight: '32px',
          width: '32px',
          height: '32px',
          borderRadius: '50%'
        },
        className: 'relative flex items-center justify-end'
      }, React.createElement(AnimatePresence, null, inputValue.length === 0 ? renderVoiceButton() : renderSubmitButton()));
    }
    if (submitIcon && !voiceIcon) {
      return renderSubmitButton();
    }
    return null;
  };
  function resolvedBoderColor() {
    if (variant === 'default' && isHovered) {
      return '#496785';
    } else if (variant === 'glow' && isHovered) {
      return undefined;
    }
    return '#c0cad5';
  }
  return React.createElement("button", {
    className: clsx('border- relative border bg-white transition-all', isMultiLine ? 'rounded-xl' : 'rounded-full', {
      'subtle-glow focus:outline-none focus:ring-0': variant === 'glow'
    }, {
      'no-glow focus:outline-none focus:ring-0': variant === 'default'
    }),
    style: {
      width: '100%',
      borderColor: resolvedBoderColor()
    },
    type: 'button',
    "aria-labelledby": 'container-wrapper-expandable-text',
    onMouseEnter: function () {
      setIsHovered(true);
    },
    onMouseLeave: function () {
      setIsHovered(false);
    }
  }, React.createElement("div", {
    style: {
      display: 'flex',
      alignItems: 'stretch'
    }
  }, React.createElement("div", {
    style: {
      display: 'flex',
      alignItems: 'center',
      flex: '90%'
    }
  }, React.createElement("textarea", {
    ref: textareaRef,
    id: id,
    value: inputValue,
    "aria-label": 'Message input',
    onChange: handleChange,
    onKeyDown: handleKeyDown,
    placeholder: placeholder,
    disabled: disabled,
    rows: 1,
    className: clsx('border-0 bg-transparent focus:outline-none focus:ring-0', disabled && 'cursor-not-allowed bg-slate-100', className),
    style: {
      resize: 'none',
      padding: '8px 16px 0',
      width: '100%',
      color: '#001833'
    }
  })), React.createElement("div", {
    style: {
      marginTop: '8px',
      display: 'flex',
      alignItems: 'flex-end',
      height: `${textareaHeight}px`,
      flex: '22px',
      position: 'relative',
      minHeight: '32px',
      maxWidth: '32px',
      minWidth: '32px',
      marginRight: '8px'
    }
  }, React.createElement("div", {
    style: {
      position: 'absolute',
      bottom: 0,
      left: '-1px',
      maxWidth: '32px'
    }
  }, renderButtons()))), React.createElement("div", {
    style: {
      height: '8px'
    }
  }));
}